import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useToastStore, type ToastTone } from '@/store/useToastStore';
import { cn } from '@/utils/cn';

/**
 * When a popup was last pressed. An open dialog decides whether a press was
 * "outside" it only after the click has finished — by which time closing the
 * popup has removed it from the page, so the press can no longer be traced to
 * the popup by looking at the DOM. The dialog asks this instead, so closing a
 * popup never closes the form behind it (and loses what was typed).
 */
let lastPopupPress = 0;
export const popupPressedJustNow = () => performance.now() - lastPopupPress < 1500;

/** Icon and tint per outcome. Text stays in the ink colours (AA on the glass). */
const TONE: Record<ToastTone, { icon: typeof Info; badge: string; edge: string; label: string }> = {
  error:   { icon: XCircle,       badge: 'bg-danger/15 text-danger-ink',   edge: 'before:bg-danger',  label: 'Error' },
  warning: { icon: AlertTriangle, badge: 'bg-warning/20 text-warning-ink', edge: 'before:bg-warning', label: 'Warning' },
  success: { icon: CheckCircle2,  badge: 'bg-success/15 text-success-ink', edge: 'before:bg-success', label: 'Done' },
  info:    { icon: Info,          badge: 'bg-accent/15 text-accent-ink',   edge: 'before:bg-accent',  label: 'Note' },
};

/**
 * The popups: small frosted cards pinned near the top of the window — errors,
 * warnings and confirmations alike, told apart by colour and icon.
 *
 * Mounted once, beside the app rather than inside it, so a popup can outlive
 * the screen switch that caused it (a form that closes on save) and shows over
 * the sign-in screens too. `pointer-events-auto` because an open dialog turns
 * pointer events off on the body, and the close button must still work over it.
 */
export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  // Always mounted, and a live region. An open dialog hides everything
  // outside itself from assistive tech — except live regions that already
  // exist when it opens. A host that appeared only with its first message
  // was hidden, and its close button with it.
  return (
    <div
      data-toast-host
      aria-live="polite"
      onPointerDownCapture={() => { lastPopupPress = performance.now(); }}
      className="pointer-events-none fixed inset-x-3 top-[3.75rem] z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:items-end lg:top-16"
    >
      {toasts.map((t) => {
        const tone = TONE[t.tone];
        const Icon = tone.icon;
        return (
          <div
            key={t.id}
            // Errors and warnings interrupt; confirmations are announced politely.
            role={t.tone === 'error' || t.tone === 'warning' ? 'alert' : 'status'}
            data-testid={`${t.tone}-popup`}
            data-tone={t.tone}
            className={cn(
              'toast-in pointer-events-auto relative flex w-full max-w-sm items-start gap-2.5 overflow-hidden rounded-lg border border-white/60 bg-white/70 py-2.5 pl-3.5 pr-2 text-fg-primary shadow-glass-lg backdrop-blur-xl backdrop-saturate-150',
              // A thin coloured edge on the left says which kind at a glance.
              "before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-['']",
              tone.edge,
            )}
          >
            <span className={cn('mt-px grid h-5 w-5 shrink-0 place-items-center rounded-full', tone.badge)}>
              <Icon size={13} strokeWidth={2.4} aria-label={tone.label} />
            </span>
            <p className="min-w-0 flex-1 break-words text-[13px] leading-5">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Close message"
              className="-my-0.5 shrink-0 rounded p-1 text-fg-tertiary transition-colors duration-120 hover:bg-black/5 hover:text-fg-primary"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
